import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';

export default function SuperAdminLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);
  const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  // Charge le script Turnstile et rend le widget invisible (uniquement si une site key est configuree)
  useEffect(() => {
    if (!SITE_KEY) return undefined;
    const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    let interval;
    const renderWidget = () => {
      if (window.turnstile && turnstileRef.current && widgetIdRef.current === null) {
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
          sitekey: SITE_KEY,
          callback: (token) => setTurnstileToken(token),
          'error-callback': () => setTurnstileToken(''),
          'expired-callback': () => setTurnstileToken(''),
        });
      }
    };
    if (window.turnstile) {
      renderWidget();
    } else if (!document.querySelector(`script[src="${SCRIPT_SRC}"]`)) {
      const s = document.createElement('script');
      s.src = SCRIPT_SRC; s.async = true; s.defer = true;
      s.onload = renderWidget;
      document.head.appendChild(s);
    } else {
      interval = setInterval(() => { if (window.turnstile) { clearInterval(interval); renderWidget(); } }, 200);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [SITE_KEY]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const r = await api.post('/super-admin/login', { email, password, turnstileToken });
      localStorage.setItem('gss_sa_token', r.data.token);
      localStorage.setItem('gss_sa_user', JSON.stringify(r.data.user));
      nav('/super-admin');
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur de connexion');
      // Token Turnstile a usage unique : reset pour permettre une nouvelle tentative
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.reset(widgetIdRef.current);
        setTurnstileToken('');
      }
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0A0A0F', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <form onSubmit={submit} style={{
        background: '#1A1A2E', borderRadius: 16, padding: 40, width: 400,
        border: '1px solid #2A2A3E', boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 11, color: '#C8A84E', letterSpacing: 4, textTransform: 'uppercase', marginBottom: 8 }}>
            Super Admin
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#FFF', letterSpacing: 2 }}>GSS-360</div>
        </div>

        {error && <div style={{ padding: 10, borderRadius: 8, background: 'rgba(220,38,38,0.15)', color: '#EF4444', fontSize: 13, marginBottom: 16 }}>{error}</div>}

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 11, color: '#888', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>Email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
            style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #333', background: '#0A0A0F', color: '#FFF', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
        </div>
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontSize: 11, color: 'var(--gss-text-light)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>Mot de passe</label>
          <div style={{ position: 'relative' }}>
            <input type={showPwd ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required
              style={{ width: '100%', padding: '10px 14px', paddingRight: 42, borderRadius: 8, border: '1px solid #333', background: '#0A0A0F', color: '#FFF', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
            <button type="button" onClick={() => setShowPwd(!showPwd)}
              style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
              aria-label={showPwd ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--gss-text-light)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {showPwd ? (
                  <>
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </>
                ) : (
                  <>
                    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
                    <path d="M14.12 14.12a3 3 0 11-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </>
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Widget Cloudflare Turnstile (invisible) — rendu uniquement si une site key est configuree */}
        {SITE_KEY && <div ref={turnstileRef} className="cf-turnstile" style={{ marginBottom: 16 }} />}

        <button type="submit" disabled={loading} style={{
          width: '100%', padding: '12px 0', borderRadius: 10, border: 'none',
          background: '#C8A84E', color: '#0A0A0F', fontWeight: 800, fontSize: 14,
          cursor: loading ? 'default' : 'pointer', letterSpacing: 1,
        }}>
          {loading ? 'Connexion...' : 'Se connecter'}
        </button>
      </form>
    </div>
  );
}
